import { Box, Divider, Stack } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { initialVideoStepValues, videoStepSchema } from './form-config';
import CreateVideoStepDto from '@settings/models/dto/create-video-step.dto';
import { useEffect, useMemo } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import JTextFieldForm from '@atoms/JTextFieldForm';
import JNumberField from '@atoms/JNumberField';
import { RadioPanelStyled } from '@styled/radio-panel-styled';
import { JButton } from '@atoms/JButton';
import {
  useCreateCvVideoStepMutation,
  useEditCvVideoStepMutation,
} from '@settings/mutations/videoStepsMutations';
import useJSnackbar from '@/hooks/useJSnackbar';
import useJDialog from '@/hooks/useJDialog';
import GetVideoStepItemDto from '@settings/models/dto/get-video-step-item.dto';

interface Props {
  defaultRank: number;
  values?: GetVideoStepItemDto;
}

const CvVideoStepForm = ({ defaultRank, values }: Props) => {
  const { t } = useTranslation();
  const {
    mutate: createCvVideoStep,
    isError: isCreateError,
    isSuccess: isCreateSuccess,
  } = useCreateCvVideoStepMutation();
  const {
    mutate: editCvVideoStep,
    isError: isEditError,
    isSuccess: isEditSuccess,
  } = useEditCvVideoStepMutation();

  const { showSnackbar } = useJSnackbar();
  const { closeJDialog } = useJDialog();
  const methods = useForm<CreateVideoStepDto>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: useMemo(
      () => initialVideoStepValues,
      [initialVideoStepValues],
    ),
    resolver: yupResolver(videoStepSchema(t)),
  });
  const { formState, setValue, getValues, trigger, handleSubmit } = methods;
  const { isValid, isDirty } = formState;

  useEffect(() => {
    setValue('rank', defaultRank);
  }, []);

  const onSubmit = handleSubmit((data) => {
    if (!values) {
      createCvVideoStep({ payload: data });
    } else {
      editCvVideoStep({ id: values.id, payload: data });
    }
  });

  useEffect(() => {
    if (!isCreateSuccess && !isEditSuccess) return;
    showSnackbar(
      t(`STEP_${values ? 'UPDATED' : 'CREATED'}_SUCCESS`),
      'success',
    );
    closeJDialog();
  }, [isCreateSuccess, isEditSuccess]);

  useEffect(() => {
    if (isCreateError || isEditError) showSnackbar(t('API_ERROR'), 'error');
  }, [isCreateError, isEditError]);
  useEffect(() => {
    if (!values) return;

    setValue('duration', values.duration);
    setValue('name', values.name);
    setValue('negativeExplanation', values.negativeExplanation);
    setValue('positiveExplanation', values.positiveExplanation);
    setValue('rank', values.rank);
    setValue('required', values.required);
    trigger();
  }, [values]);
  return (
    <Box>
      <FormProvider {...methods}>
        <form onSubmit={onSubmit} autoComplete="off" noValidate>
          <Stack gap={1.8} p={2}>
            <JTextFieldForm
              required
              name="name"
              data-qa="name"
              fullWidth
              noBorder
              label={t('STEP_NAME')}
            />
            <JNumberField
              defaultValue={values?.duration}
              onNumberChange={(value) => {
                setValue('duration', value);
                trigger('duration');
              }}
              name="duration"
              fullWidth
              label={t('DURATION_STEP')}
              required
              noBorder
              helperText="La limite maximale ne peut pas dépasser 60 secondes"
            />

            <JTextFieldForm
              multiline
              required
              name="positiveExplanation"
              data-qa="name"
              fullWidth
              noBorder
              label={t('TO_DO')}
              rows={2}
            />

            <JTextFieldForm
              multiline
              required
              name="negativeExplanation"
              fullWidth
              noBorder
              label={t('TO_AVOID')}
              rows={2}
            />
            <Stack direction={'row'} gap={1}>
              <RadioPanelStyled
                data-qa="false"
                onClick={() => {
                  setValue('required', false);
                  trigger('required');
                }}
                selected={!getValues('required')}
              >
                {t('OPTIONAL_STEP_2')}
              </RadioPanelStyled>
              <RadioPanelStyled
                data-qa="true"
                onClick={() => {
                  setValue('required', true);
                  trigger('required');
                }}
                selected={getValues('required')}
              >
                {t('REQUIRED_STEP_2')}
              </RadioPanelStyled>
            </Stack>
          </Stack>
          <Divider />
          <Stack p={2} alignItems="end">
            <JButton
              disabled={!isValid || (!isDirty && !values)}
              type="submit"
              variant="contained"
              color="secondary"
            >
              {values ? t('EDIT_STEP') : t('CREATE_STEP')}
            </JButton>
          </Stack>
        </form>
      </FormProvider>
    </Box>
  );
};

export default CvVideoStepForm;
